import { useCallback } from 'react';
import {
  presign,
  uploadToS3,
  confirm,
  validateImageFile,
  type MediaCategory,
} from '@/lib/upload';

/**
 * 펀딩 상세 본문 에디터 이미지 업로드용 presign 카테고리.
 * be-settlement/미디어 서비스가 수용하는 값과 일치해야 한다.
 * 펀딩 커버/상세 이미지 업로드(FundingCampaignCreatePage ImageUploadField/ImageGalleryField)와
 * 동일한 FUNDING_COVER 카테고리를 공유한다.
 */
const FUNDING_IMAGE_CATEGORY: MediaCategory = 'FUNDING_COVER';

/**
 * RichTextEditor 의 onUploadImage 에 그대로 넘길 수 있는 업로드 함수를 반환한다.
 * presign → S3 직접 PUT → confirm 3단계를 거쳐 publicUrl 을 돌려준다.
 * 클라이언트 선검증 실패/네트워크 오류는 한국어 메시지로 throw 하므로
 * 호출부(RichTextEditor onUploadError)가 표면화한다.
 *
 * useProductDetailImageUpload / useEventImageUpload 와 1:1 동일한 패턴이며 카테고리만 FUNDING_COVER 이다.
 */
export const useFundingDetailImageUpload = (): ((file: File) => Promise<string>) => {
  return useCallback(async (file: File): Promise<string> => {
    const validationError = validateImageFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const [slot] = await presign([{ file, category: FUNDING_IMAGE_CATEGORY }]);
    if (!slot) {
      throw new Error('업로드 URL 발급 응답이 올바르지 않습니다.');
    }

    await uploadToS3([{ item: slot, file }]);

    const confirmed = await confirm([slot.fileId]);
    const publicUrl = confirmed.get(slot.fileId) ?? slot.publicUrl;
    if (!publicUrl) {
      throw new Error('업로드한 이미지 주소를 확인하지 못했습니다.');
    }
    return publicUrl;
  }, []);
};
